import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface EmailChannelConfig {
  id: string;
  tenantId: string;
  ingestAddress: string;
  webhookSecret: string;
  jobTypeId: string;
  isEnabled: boolean;
  // R-266 6a — when true, inbound email creates a typeless batch routed by
  // document fingerprint rather than the configured default job type.
  routeByContent: boolean;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════

export function useEmailChannel() {
  const [config, setConfig] = useState<EmailChannelConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Server can legitimately return `null` (no config exists for tenant)
      // OR a body without the `config` key. Defensive access keeps the hook
      // out of "Cannot read properties of null (reading 'config')" land. (#76)
      const data = await api.get<{ config: EmailChannelConfig | null } | null>("/email-channel");
      setConfig(data?.config ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (data: Partial<Omit<EmailChannelConfig, "id" | "tenantId" | "createdAt" | "updatedAt">>): Promise<EmailChannelConfig> => {
    const result = await api.put<EmailChannelConfig>("/email-channel", data);
    await load();
    return result;
  };

  const remove = async (): Promise<void> => {
    await api.delete("/email-channel");
    setConfig(null);
  };

  const generateSecret = async (): Promise<{ secret: string }> => {
    return api.post<{ secret: string }>("/email-channel/generate-secret", {});
  };

  return { config, isLoading, error, refresh: load, save, remove, generateSecret };
}
